import React, { useState, useEffect } from 'react';

export function TreeConnectors({ generations, nodeMap, genMap, containerRef }) {
  const [lines, setLines] = useState([]);

  useEffect(() => {
    if (!containerRef.current) return;

    const timer = setTimeout(() => {
      const container = containerRef.current;
      if (!container) return;
      const cRect = container.getBoundingClientRect();
      const newLines = [];

      // Draw partner connections (horizontal)
      const drawnPartners = new Set();
      Object.values(nodeMap).forEach(node => {
        if (!node.partner_id || drawnPartners.has(node.node_id)) return;
        drawnPartners.add(node.node_id);
        drawnPartners.add(node.partner_id);

        const el1 = container.querySelector(`[data-testid="tree-node-${node.node_id}"]`);
        const el2 = container.querySelector(`[data-testid="tree-node-${node.partner_id}"]`);
        if (!el1 || !el2) return;

        const r1 = el1.getBoundingClientRect();
        const r2 = el2.getBoundingClientRect();
        const x1 = r1.left + r1.width / 2 - cRect.left;
        const y1 = r1.top + r1.height / 2 - cRect.top;
        const x2 = r2.left + r2.width / 2 - cRect.left;
        const y2 = r2.top + r2.height / 2 - cRect.top;

        newLines.push({ x1, y1, x2, y2, type: 'partner' });
      });

      // Draw parent-child connections (vertical)
      Object.values(nodeMap).forEach(child => {
        (child.parent_ids || []).forEach(pid => {
          const parentNode = nodeMap[pid];
          if (!parentNode) return;

          const parentEl = container.querySelector(`[data-testid="tree-node-${pid}"]`);
          const childEl = container.querySelector(`[data-testid="tree-node-${child.node_id}"]`);
          if (!parentEl || !childEl) return;

          const pr = parentEl.getBoundingClientRect();
          const cr = childEl.getBoundingClientRect();

          // If parent has a partner, draw from midpoint between them
          let px, py;
          if (parentNode.partner_id && nodeMap[parentNode.partner_id]) {
            const partnerEl = container.querySelector(`[data-testid="tree-node-${parentNode.partner_id}"]`);
            if (partnerEl) {
              const ptr = partnerEl.getBoundingClientRect();
              px = (pr.left + pr.width / 2 + ptr.left + ptr.width / 2) / 2 - cRect.left;
              py = Math.max(pr.bottom, ptr.bottom) - cRect.top + 2;
            } else {
              px = pr.left + pr.width / 2 - cRect.left;
              py = pr.bottom - cRect.top + 2;
            }
          } else {
            px = pr.left + pr.width / 2 - cRect.left;
            py = pr.bottom - cRect.top + 2;
          }

          const cx = cr.left + cr.width / 2 - cRect.left;
          const cy = cr.top - cRect.top - 2;

          newLines.push({ x1: px, y1: py, x2: cx, y2: cy, type: 'parent-child' });
        });
      });

      setLines(newLines);
    }, 100);

    return () => clearTimeout(timer);
  }, [generations, nodeMap, genMap, containerRef]);

  return (
    <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ zIndex: 0 }}>
      <defs>
        <marker id="heart-dot" viewBox="0 0 10 10" refX="5" refY="5" markerWidth="6" markerHeight="6">
          <circle cx="5" cy="5" r="3" fill="#f472b6" />
        </marker>
      </defs>
      {lines.map((line, i) => {
        if (line.type === 'partner') {
          return (
            <line
              key={`p-${i}`}
              x1={line.x1} y1={line.y1}
              x2={line.x2} y2={line.y2}
              stroke="#f9a8d4"
              strokeWidth="2"
              strokeDasharray="6 4"
              opacity="0.8"
            />
          );
        }
        // Parent-child: curved line
        const midY = (line.y1 + line.y2) / 2;
        return (
          <path
            key={`c-${i}`}
            d={`M ${line.x1} ${line.y1} C ${line.x1} ${midY}, ${line.x2} ${midY}, ${line.x2} ${line.y2}`}
            stroke="#94a3b8"
            strokeWidth="2"
            fill="none"
            opacity="0.6"
          />
        );
      })}
    </svg>
  );
}
